import { Decimal } from 'decimal.js';

/** 数量最大小数位，与后端 CpInvoiceRegisterQuantityHelper.QUANTITY_SCALE 一致 */
const QUANTITY_SCALE = 3;
/** 金额最大小数位 */
const AMOUNT_SCALE = 2;

/**
 * 安全转为 Decimal，无效值返回 null。
 * 调用方：本模块各数量/金额方法
 * @param {*} val 原始值
 * @returns {Decimal|null}
 */
export function toCpDecimal(val) {
  if (val === null || val === undefined || val === '') return null;
  try {
    const d = new Decimal(val);
    if (!d.isFinite()) return null;
    return d;
  } catch (e) {
    return null;
  }
}

/**
 * 收敛 JSON number 浮点误差后再截断/舍入。
 * 调用方：truncateCpQuantity、toCpAmountNumber
 * @param {Decimal} d 已解析 Decimal
 * @param {number} scale 目标小数位
 * @param {number} rounding 截断/舍入模式
 * @returns {number}
 */
function toScaledNumber(d, scale, rounding) {
  const compensated = d.toDecimalPlaces(scale + 3, Decimal.ROUND_HALF_UP);
  return compensated.toDecimalPlaces(scale, rounding).toNumber();
}

/**
 * 数量向零截断至 3 位（不四舍五入），与后端 CpInvoiceRegisterQuantityHelper.normalize 一致。
 * 调用方：salePurchaseOrderDisplay 登记发票数量处理
 * @param {*} val 原始数量
 * @returns {number}
 */
export function truncateCpQuantity(val) {
  const d = toCpDecimal(val);
  if (!d || d.lte(0)) return 0;
  // 调用 toScaledNumber：先收敛 JSON 浮点误差，再向零截断至 3 位
  return toScaledNumber(d, QUANTITY_SCALE, Decimal.ROUND_DOWN);
}

/**
 * 数量展示：截断后去掉尾部无意义 0。
 * 调用方：salePurchaseOrderDisplay 采购明细表数量列
 * @param {*} val 原始数量
 * @returns {string}
 */
export function formatCpQuantity(val) {
  const n = truncateCpQuantity(val);
  if (n === 0) return '0';
  return String(n).replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, '');
}

/**
 * 金额保留 2 位（四舍五入），返回 number。
 * 调用方：salePurchaseOrderDisplay 金额计算与提交
 * @param {*} val 原始金额
 * @returns {number}
 */
export function toCpAmountNumber(val) {
  const d = toCpDecimal(val);
  if (!d) return 0;
  // 调用 toScaledNumber：先收敛 JSON 浮点误差，再四舍五入至 2 位
  return toScaledNumber(d, AMOUNT_SCALE, Decimal.ROUND_HALF_UP);
}

/**
 * 金额展示：固定 2 位小数。
 * 调用方：salePurchaseOrderDisplay 金额列与小计
 * @param {*} val 原始金额
 * @returns {string}
 */
export function formatCpAmount(val) {
  return toCpAmountNumber(val).toFixed(AMOUNT_SCALE);
}

/**
 * 金额相减并保留 2 位（四舍五入）。
 * 调用方：salePurchaseOrderDisplay 税额、OCR、提交
 * @param {*} a 被减数
 * @param {*} b 减数
 * @returns {number}
 */
export function subtractCpAmount(a, b) {
  const da = toCpDecimal(a) || new Decimal(0);
  const db = toCpDecimal(b) || new Decimal(0);
  return da.sub(db).toDecimalPlaces(AMOUNT_SCALE, Decimal.ROUND_HALF_UP).toNumber();
}

/**
 * 累加金额并保留 2 位（四舍五入）。
 * 调用方：salePurchaseOrderDisplay 发票/明细小计
 * @param {Array<*>} values 金额列表
 * @returns {number}
 */
export function sumCpAmounts(values) {
  const total = (values || []).reduce((acc, v) => {
    const d = toCpDecimal(v);
    return d ? acc.add(d) : acc;
  }, new Decimal(0));
  return total.toDecimalPlaces(AMOUNT_SCALE, Decimal.ROUND_HALF_UP).toNumber();
}

/**
 * 将采购税率统一为小数（0.13）；大于 1 时按百分数（13）处理。
 * 调用方：calcCpInvoiceLineAmountPartsFromTaxUnit
 * @param {*} taxRate 税率
 * @returns {number}
 */
export function normalizeCpTaxRateDecimal(taxRate) {
  const d = toCpDecimal(taxRate);
  if (!d || d.lte(0)) return 0;
  if (d.gt(1)) {
    return d.div(100).toDecimalPlaces(6, Decimal.ROUND_HALF_UP).toNumber();
  }
  return d.toNumber();
}

/**
 * 按含税单价拆分行价税：含税=数量×含税单价；未税=含税/(1+r)；税额=含税−未税。
 * 与后端 CpInvoiceRegisterTaxHelper.calcFromQtyAndTaxUnitPrice 一致。
 * 调用方：salePurchaseOrderDisplay.calcInvoiceLineAmountPartsFromTaxUnit
 * @param {*} qty 开票数量
 * @param {*} taxUnitPrice 含税单价
 * @param {*} taxRate 税率
 * @returns {{ amountWithTax: number, amountWithoutTax: number, taxAmount: number }}
 */
export function calcCpInvoiceLineAmountPartsFromTaxUnit(qty, taxUnitPrice, taxRate) {
  const q = new Decimal(truncateCpQuantity(qty));
  const unit = toCpDecimal(taxUnitPrice) || new Decimal(0);
  const rateDecimal = normalizeCpTaxRateDecimal(taxRate);
  const amountWithTax = q.mul(unit).toDecimalPlaces(AMOUNT_SCALE, Decimal.ROUND_HALF_UP);
  if (rateDecimal <= 0) {
    const amt = amountWithTax.toNumber();
    return { amountWithTax: amt, amountWithoutTax: amt, taxAmount: 0 };
  }
  const rate = new Decimal(rateDecimal);
  const amountWithoutTax = amountWithTax.div(rate.add(1)).toDecimalPlaces(AMOUNT_SCALE, Decimal.ROUND_HALF_UP);
  const taxAmount = amountWithTax.sub(amountWithoutTax).toDecimalPlaces(AMOUNT_SCALE, Decimal.ROUND_HALF_UP);
  return {
    amountWithTax: amountWithTax.toNumber(),
    amountWithoutTax: amountWithoutTax.toNumber(),
    taxAmount: taxAmount.toNumber()
  };
}

/**
 * 截断后数量是否相等。
 * 调用方：salePurchaseOrderDisplay.shouldUseLastSliceAmounts
 * @param {*} a 数量 a
 * @param {*} b 数量 b
 * @returns {boolean}
 */
export function cpQuantityEquals(a, b) {
  return truncateCpQuantity(a) === truncateCpQuantity(b);
}

/**
 * 截断后数量 a 是否大于 b。
 * 调用方：salePurchaseOrderDisplay.submitInvoice 校验
 * @param {*} a 数量 a
 * @param {*} b 数量 b
 * @returns {boolean}
 */
export function cpQuantityGt(a, b) {
  return truncateCpQuantity(a) > truncateCpQuantity(b);
}

/**
 * 截断后数量是否大于 0。
 * 调用方：salePurchaseOrderDisplay.submitInvoice 过滤
 * @param {*} val 数量
 * @returns {boolean}
 */
export function cpQuantityPositive(val) {
  return truncateCpQuantity(val) > 0;
}

/**
 * 两金额在 2 位精度下是否相等。
 * 调用方：salePurchaseOrderDisplay OCR 快照比对
 * @param {*} a 金额 a
 * @param {*} b 金额 b
 * @returns {boolean}
 */
export function cpAmountEquals(a, b) {
  return toCpAmountNumber(a) === toCpAmountNumber(b);
}
